import React, { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { getErrorMessage } from "services/api";
import { getItemGroups } from "features/Products/api";
import { buildGroupOptions } from "features/Catalog/groupOptions";

import { Button } from "components/Button";
import { Input } from "components/Input";
import { Select } from "components/Select";
import { Table } from "components/Table";
import { MediumText, RegularText } from "components/Text";
import { VerticalSpace } from "components/Spacing";

import {
  getDiscountItemGroups,
  createDiscountItemGroup,
  deleteDiscountItemGroup,
} from "./api";
import styles from "./styles.module.scss";

/** Скидки по группам товаров конкретного клиента */
export const ClientDiscountsSection = ({ clientid }) => {
  const queryClient = useQueryClient();

  const discountsQuery = useQuery(["client-discounts", clientid], () =>
    getDiscountItemGroups(clientid)
  );
  const discounts = useMemo(
    () => discountsQuery.data?.items ?? [],
    [discountsQuery.data]
  );

  const groupsQuery = useQuery(["item-groups"], () => getItemGroups());
  const groups = groupsQuery.data?.items ?? [];
  const groupOptions = useMemo(
    () => buildGroupOptions(groups, { withRoot: false }),
    [groups]
  );

  const [selectedId, setSelectedId] = useState(null);
  const [showAddFields, setShowAddFields] = useState(false);
  const [groupOption, setGroupOption] = useState(null);
  const [discount, setDiscount] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const rowData = useMemo(
    () =>
      discounts.map((d) => ({
        clientdiscountitemgroupid: d.clientdiscountitemgroupid,
        groupName:
          groups.find((g) => String(g.itemgroupid) === String(d.itemgroupid))
            ?.name ?? d.itemgroupid,
        value: d.value,
      })),
    [discounts, groups]
  );

  const colDefs = useMemo(
    () => [
      { field: "clientdiscountitemgroupid", headerName: "№", width: 90 },
      { field: "groupName", headerName: "Группа товаров", flex: 5 },
      { field: "value", headerName: "Скидка", flex: 1 },
    ],
    []
  );

  const handleSave = async () => {
    if (!groupOption || discount === "") return;
    setSaving(true);
    setError("");
    try {
      await createDiscountItemGroup({
        itemgroupid: Number(groupOption.value),
        value: Number(discount),
        clientid: Number(clientid),
      });
      setShowAddFields(false);
      setGroupOption(null);
      setDiscount("");
      queryClient.invalidateQueries(["client-discounts", clientid]);
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedId) return;
    setError("");
    try {
      await deleteDiscountItemGroup(selectedId);
      setSelectedId(null);
      queryClient.invalidateQueries(["client-discounts", clientid]);
    } catch (e) {
      setError(getErrorMessage(e));
    }
  };

  return (
    <div>
      <MediumText>Скидки по группам товаров</MediumText>
      <VerticalSpace size={8} />
      <Table
        height={200}
        rowData={rowData}
        colDefs={colDefs}
        rowKey="clientdiscountitemgroupid"
        selectedId={selectedId}
        onSelect={(row) =>
          setSelectedId((prev) =>
            prev === row.clientdiscountitemgroupid
              ? null
              : row.clientdiscountitemgroupid
          )
        }
        loading={discountsQuery.isLoading}
        error={
          discountsQuery.isError ? getErrorMessage(discountsQuery.error) : ""
        }
        onRetry={discountsQuery.refetch}
      />
      <VerticalSpace size={8} />
      {showAddFields && (
        <div className={styles.addDiscountRow}>
          <Select
            label="Группа товаров"
            labelWidth={120}
            options={groupOptions}
            value={groupOption}
            onChange={setGroupOption}
            width={280}
          />
          <Input
            label="Скидка (%)"
            labelWidth={120}
            type="number"
            value={discount}
            onChange={(ev) => setDiscount(ev.target.value)}
            inputWidth={90}
          />
        </div>
      )}
      {!!error && (
        <RegularText size={13} color="danger">
          {error}
        </RegularText>
      )}
      <VerticalSpace size={8} />
      <div className={styles.sectionActions}>
        {showAddFields ? (
          <Button
            icon="galochka"
            variant="secondary"
            onClick={handleSave}
            disabled={!groupOption || discount === ""}
            loading={saving}
          >
            Сохранить
          </Button>
        ) : (
          <Button icon="plus" onClick={() => setShowAddFields(true)}>
            Добавить
          </Button>
        )}
        <Button
          icon="delete"
          onClick={handleDelete}
          disabled={!selectedId}
          title="Удалить скидку"
        />
      </div>
    </div>
  );
};
